import { Component, OnInit } from '@angular/core';
import { BackendService } from '../../services/backend.service';
import {
  Facility,
  Detection,
  ManualDetectionSummarySet,
} from '../../types/common.types';
import { NotificationService } from '../../services/notification.service';
import { catchError, finalize } from 'rxjs/operators';
import { of } from 'rxjs';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-date-facility-flow',
  templateUrl: './date-facility-flow.component.html',
  styleUrl: './date-facility-flow.component.css',
})
export class DateFacilityFlowComponent implements OnInit {
  facilities: Facility[] = [];
  isLoading = false;
  detectionCounts: Record<number, number | null> = {};
  inputCounts: Record<number, string> = {};
  isDetectionLoading = false;
  showCalendar = false;
  facilitiesUnderUpdate: Set<number> = new Set<number>();
  private countInputSubject = new Subject<{
    facility: Facility;
    value: string;
  }>();
  currentYear: number;
  currentMonth: number;
  daysInMonth: number[] = [];
  selectedDay: number | null;
  readonly monthNames: string[] = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  selectedTime: Date;

  /**
   * Map of facilityId -> hour (0-23) -> detection object (or null if none)
   */
  private facilityHourlyDetections: Record<
    number,
    Record<number, Detection | null>
  > = {};

  constructor(
    private backendService: BackendService,
    private notificationService: NotificationService,
    private router: Router
  ) {
    const now = new Date();
    this.currentYear = now.getFullYear();
    this.currentMonth = now.getMonth();
    this.selectedDay = now.getDate();
    this.selectedTime = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      this.clampHour(now.getHours()),
      0,
      0,
      0
    );
  }

  ngOnInit(): void {
    this.isLoading = true;

    this.countInputSubject
      .pipe(
        debounceTime(500),
        distinctUntilChanged(
          (prev, curr) =>
            prev.facility.id === curr.facility.id && prev.value === curr.value
        )
      )
      .subscribe(({ facility, value }) => {
        this.processCountSave(facility, value);
      });

    this.backendService
      .facilities()
      .pipe(
        catchError(() => of([])),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe((facilities) => {
        this.facilities = facilities;
        this.generateDaysInMonth();
        this.fetchAllDetectionCounts();
      });
  }

  private clampHour(hour: number): number {
    return Math.max(8, Math.min(15, hour));
  }

  public getIntervalEnd(): Date {
    const end = new Date(this.selectedTime);
    end.setHours(this.selectedTime.getHours() + 1);
    return end;
  }

  generateDaysInMonth(): void {
    const numDays = new Date(
      this.currentYear,
      this.currentMonth + 1,
      0
    ).getDate();
    this.daysInMonth = Array.from({ length: numDays }, (_, i) => i + 1);
  }

  prevMonth(): void {
    this.currentMonth--;
    if (this.currentMonth < 0) {
      this.currentMonth = 11;
      this.currentYear--;
    }
    this.generateDaysInMonth();
    if (!this.selectedDay || this.selectedDay > this.daysInMonth.length) {
      this.selectedDay = null;
    }
  }

  nextMonth(): void {
    this.currentMonth++;
    if (this.currentMonth > 11) {
      this.currentMonth = 0;
      this.currentYear++;
    }
    this.generateDaysInMonth();
    if (!this.selectedDay || this.selectedDay > this.daysInMonth.length) {
      this.selectedDay = null;
    }
  }

  isFutureDay(day: number): boolean {
    const today = new Date();
    const date = new Date(this.currentYear, this.currentMonth, day);
    return date > today;
  }

  isNextMonthFuture(): boolean {
    const nextMonth = this.currentMonth === 11 ? 0 : this.currentMonth + 1;
    const nextYear =
      this.currentMonth === 11 ? this.currentYear + 1 : this.currentYear;
    const today = new Date();
    return (
      nextYear > today.getFullYear() ||
      (nextYear === today.getFullYear() && nextMonth > today.getMonth())
    );
  }

  isNextHourFuture(): boolean {
    const nextHour = new Date(this.selectedTime);
    nextHour.setHours(nextHour.getHours() + 1);
    return nextHour > new Date();
  }

  selectDay(day: number): void {
    if (this.isFutureDay(day)) return;
    this.selectedDay = day;
    // Clamp hour to 8-15 when changing day
    const hour = this.clampHour(this.selectedTime.getHours());
    this.selectedTime = new Date(
      this.currentYear,
      this.currentMonth,
      this.selectedDay,
      hour,
      0,
      0,
      0
    );
    this.fetchAllDetectionCounts();
  }

  selectDayAndCollapse(day: number): void {
    this.selectDay(day);
    this.showCalendar = false;
  }

  incrementHour(): void {
    if (this.selectedTime.getHours() >= 23) return;

    const nextHour = new Date(this.selectedTime);
    nextHour.setHours(nextHour.getHours() + 1);
    if (nextHour > new Date()) return;
    this.selectedTime = nextHour;
    this.updateCountsForSelectedHour();
  }

  decrementHour(): void {
    if (this.selectedTime.getHours() <= 0) return;

    this.selectedTime.setHours(this.selectedTime.getHours() - 1);
    this.selectedTime = new Date(this.selectedTime);
    this.updateCountsForSelectedHour();
  }

  canIncrementHour(): boolean {
    return (
      this.selectedTime.getHours() < 23 && !this.updatingDetectionsOnFacility()
    );
  }

  canDecrementHour(): boolean {
    return (
      this.selectedTime.getHours() > 0 && !this.updatingDetectionsOnFacility()
    );
  }

  updatingDetectionsOnFacility(): boolean {
    return this.facilitiesUnderUpdate.size > 0;
  }

  /**
   * Fetches all detections for all facilities for the selected day using allDetections.
   * Populates detectionCounts and inputCounts for each facility.
   */
  fetchAllDetectionCounts(): void {
    if (!this.facilities.length) return;
    this.detectionCounts = {};
    this.inputCounts = {};
    this.facilityHourlyDetections = {};
    this.isDetectionLoading = true;

    const dayStart = new Date(this.selectedTime);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(this.selectedTime);
    dayEnd.setHours(23, 59, 59, 999);

    this.backendService
      .allDetections(dayStart, dayEnd, 1, 'days')
      .pipe(
        catchError(() => {
          this.notificationService.showInfo('No registrations on this day');
          return of([]);
        }),
        finalize(() => {
          this.isDetectionLoading = false;
        })
      )
      .subscribe((detectionSet: ManualDetectionSummarySet[]) => {
        // Build facilityHourlyDetections map
        for (const facility of this.facilities) {
          this.facilityHourlyDetections[facility.id] = {};
          const summary = detectionSet.find(
            (s) => s.facilityId === facility.id
          );
          if (
            summary &&
            summary.manualDetections &&
            Array.isArray(summary.manualDetections)
          ) {
            for (const d of summary.manualDetections) {
              const start = new Date(d.startTime);
              this.facilityHourlyDetections[facility.id][start.getHours()] = d;
            }
          }
        }
        this.updateCountsForSelectedHour();
      });
  }

  canEditCounts(facilityId: number): boolean {
    const hour = this.selectedTime.getHours();
    const detection = this.facilityHourlyDetections[facilityId]?.[hour] || null;

    if (!detection) {
      return false;
    }
    return true;
  }

  /**
   * Updates detectionCounts and inputCounts for the currently selected hour using the local map.
   */
  private updateCountsForSelectedHour(): void {
    for (const facility of this.facilities) {
      const hour = this.selectedTime.getHours();
      const detection =
        this.facilityHourlyDetections[facility.id]?.[hour] || null;
      const count =
        detection && detection.count !== undefined ? detection.count : null;
      this.detectionCounts[facility.id] = count;
      this.inputCounts[facility.id] =
        count !== null && count !== undefined ? String(count) : '';
    }
  }

  onCountInputChange(facility: Facility, event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input ? input.value : '';
    this.inputCounts[facility.id] = value;

    this.countInputSubject.next({ facility, value });
  }

  /**
   * Handles changes to the count input for a facility.
   * Validates input, updates or creates detection, and refreshes the count.
   */
  private processCountSave(facility: Facility, value: string): void {
    // Don't save if input is cleared
    if (value === '') return;

    const count = Number(value);

    // Validate input
    if (isNaN(count)) {
      this.notificationService.showError('Please enter a valid number');
      return;
    }

    if (count < 0) {
      this.notificationService.showError('Count cannot be negative');
      return;
    }

    this.facilitiesUnderUpdate.add(facility.id);
    // Prepare date range for the selected day
    const dayStart = new Date(this.selectedTime);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(this.selectedTime);
    dayEnd.setHours(23, 59, 59, 999);

    // Fetch detections for the facility and day
    this.backendService
      .detections(facility.id, dayStart, dayEnd, 1, 'days')
      .pipe(catchError(() => of([])))
      .subscribe((detections: Detection[]) => {
        // Find detection for the selected hour
        const match = detections.find((d) => {
          const start = new Date(d.startTime);
          return (
            start.getHours() === this.selectedTime.getHours() &&
            start.getDate() === this.selectedTime.getDate() &&
            start.getMonth() === this.selectedTime.getMonth() &&
            start.getFullYear() === this.selectedTime.getFullYear()
          );
        });

        // Update existing detection if found, otherwise create new
        const detectionId = match?.manualDetectionDtos?.[0]?.id;
        const saveOrUpdate$ = detectionId
          ? this.backendService.setDetections(
              detectionId,
              facility.organization_id,
              facility.department_id,
              facility.id,
              count,
              this.selectedTime
            )
          : this.backendService.createDetection(
              count,
              facility.organization_id,
              facility.department_id,
              facility.id,
              this.selectedTime
            );

        saveOrUpdate$
          .pipe(
            catchError(() => {
              this.notificationService.showError(
                detectionId ? 'Failed to update count' : 'Failed to save count'
              );
              return of(null);
            }),
            finalize(() => this.fetchDetectionCountForFacility(facility))
          )
          .subscribe((response) => {
            if (response) {
              this.notificationService.showSuccess(
                detectionId
                  ? 'Count updated successfully'
                  : 'Count saved successfully'
              );
            }
          })
          .add(() => {
            this.facilitiesUnderUpdate.delete(facility.id);
          });
      });
  }

  /**
   * Fetches all detections for a single facility for the selected day and updates the local map and UI for that facility.
   */
  private fetchDetectionCountForFacility(facility: Facility): void {
    const dayStart = new Date(this.selectedTime);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(this.selectedTime);
    dayEnd.setHours(23, 59, 59, 999);
    this.backendService
      .detections(facility.id, dayStart, dayEnd, 1, 'days')
      .pipe(catchError(() => of([])))
      .subscribe((detections: Detection[]) => {
        // Update the local map for this facility
        this.facilityHourlyDetections[facility.id] = {};
        for (const d of detections) {
          const start = new Date(d.startTime);
          this.facilityHourlyDetections[facility.id][start.getHours()] = d;
        }
        // Update UI for the selected hour
        const hour = this.selectedTime.getHours();
        const detection =
          this.facilityHourlyDetections[facility.id][hour] || null;
        const count =
          detection && detection.count !== undefined ? detection.count : null;
        this.detectionCounts[facility.id] = count;
        this.inputCounts[facility.id] =
          count !== null && count !== undefined ? String(count) : '';
      });
  }

  logout(): void {
    window.localStorage.clear();
    this.router.navigate(['/login']);
  }

  goToCountFlow() {
    this.router.navigate(['/facility-flow']);
  }
}
